import { some } from "lodash";
import { Button } from "PFComponents/button";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { Typography } from "PFComponents/typography";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import React, { useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SkillsFrameworkData } from "../../../skills_frameworks.types";
import pageCss from "../../skills_frameworks_edit_page.module.scss";
import { defaultCustomFieldRow, ENTRIES_LIMIT, FIELD_ARRAY_NAME } from "./custom_fields_section.consts";
import css from "./custom_fields_section.module.scss";
import { SingleFormRow } from "./single_form_row";

export const CustomFieldsSection = (): JSX.Element => {
  const { t } = useTranslation("skillsFrameworks", { keyPrefix: "edit" });
  const { customTypes } = useCustomTypes();
  const { control } = useFormContext<SkillsFrameworkData>();
  const customFieldsValues = useWatch<SkillsFrameworkData>({
    name: "customFields",
    defaultValue: []
  }) as SkillsFrameworkData["customFields"];

  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELD_ARRAY_NAME
  });

  const skillsFrameworkCustomTypes = useMemo(
    () => customTypes.filter(({ sections }) => some(sections, ({ key }) => key === "skills_framework")),
    [customTypes]
  );

  const customTypesOptions: DropdownOption[] = useMemo(
    () =>
      skillsFrameworkCustomTypes.map(({ id, name, display_as: displayAs }) => ({
        id,
        displayElement: displayAs || name,
        item: id
      })),
    [skillsFrameworkCustomTypes]
  );

  const entriesCount = customFieldsValues.length;
  const showAddButton = entriesCount < ENTRIES_LIMIT && entriesCount < customTypesOptions.length;

  return (
    <>
      <Typography variant="h4" className={pageCss.sectionTitle}>
        {t("customFields")}
      </Typography>
      <div className={css.rowsWrapper}>
        {fields.map((item, index) => (
          <SingleFormRow
            key={item.id}
            customTypes={skillsFrameworkCustomTypes}
            customTypesOptions={customTypesOptions}
            rowIndex={index}
            onRemove={() => remove(index)}
          />
        ))}
      </div>
      {showAddButton && (
        <Button
          icon="add"
          text={t("addCustomField")}
          type="button"
          kind="text"
          onClick={() => {
            append(defaultCustomFieldRow);
          }}
          className={css.addButton}
        />
      )}
    </>
  );
};
